import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { useDateFormat } from '@/composables/dateFormat';
import { useSweetAlert } from '@/composables/sweetalert';
import { useStore } from 'vuex';
import { Project } from '@/store/project/project.model';
import { ProjectGetter } from '@/store/project/project.module';
import { Procure } from '@/store/procure/procure.model';
import { ProcureAction, ProcureGetter } from '@/store/procure/procure.module';
import { AccountGetter } from '@/store/account/account.module';
import { Role } from '@/store/role/role.model';
import ProjectProcureFormComponent from '@/components/project/procure/projectProcureForm/ProjectProcureForm.vue';
import ProjectProcureEndDateOffcanvasComponent from '../projectProcureEndDateOffcanvas/ProjectProcureEndDateOffcanvas.vue';
import ProjectProcureEndDateModalComponent from '../projectProcureEndDateModal/ProjectProcureEndDateModal.vue';
import ProjectProcureEndDateFormComponent from '../projectProcureEndDateForm/ProjectProcureEndDateForm.vue';
import ProjectProcureAttachmentOffcanvasComponent from '../projectProcureAttachmentOffcanvas/ProjectProcureAttachmentOffcanvas.vue';
import ProjectProcureAttachmentModalComponent from '../projectProcureAttachmentModal/ProjectProcureAttachmentModal.vue';
import ProjectProcureAttachmentFormComponent from '../projectProcureAttachmentForm/ProjectProcureAttachmentForm.vue';
import ProjectProcureImageModalComponent from '../projectProcureImageModal/ProjectProcureImageModal.vue';

export default defineComponent({
  name: 'ProjectProcureComponent',
  components: {
    ProjectProcureFormComponent,
    ProjectProcureEndDateOffcanvasComponent,
    ProjectProcureEndDateModalComponent,
    ProjectProcureEndDateFormComponent,
    ProjectProcureAttachmentOffcanvasComponent,
    ProjectProcureAttachmentModalComponent,
    ProjectProcureAttachmentFormComponent,
    ProjectProcureImageModalComponent,
  },
  props: {
    projectId: {
      type: Number
    }
  },
  emits: [
    'refresh'
  ],
  setup: (props, context) => {
    const swal = useSweetAlert();
    const dateFormat = useDateFormat();
    const store = useStore();
    const role = computed<Role | undefined>(() => store.getters[AccountGetter.SINGLE_ROLE]);
    const canEdit = computed(() => role.value?.name === 'เจ้าหน้าที่ ส.กกภ.' || role.value?.name === 'เจ้าหน้าที่ประจำจังหวัด');
    const project = computed<Project>(() => store.getters[ProjectGetter.BY_ID](props.projectId));
    const procure = computed<Procure | null>(() => store.getters[ProcureGetter.GET]);
    const isCanceled = computed(() => project.value?.isCanceled);
    const isStarted = computed(() => project.value?.projectStatus.key === 1);
    const isProcured = computed(() => project.value?.projectStatus.key > 1);
    const isCompleted = computed(() => project.value?.projectStatus.key >= 5);
    const isFinished = computed(() => project.value?.isFinished);
    const projectProcureForm = ref<InstanceType<typeof ProjectProcureFormComponent>>();
    const endDateOffcanvas = ref<InstanceType<typeof ProjectProcureEndDateOffcanvasComponent>>();
    const endDateModal = ref<InstanceType<typeof ProjectProcureEndDateModalComponent>>();
    const endDateForm = ref<InstanceType<typeof ProjectProcureEndDateFormComponent>>();
    const selectedEndDate = ref<number | null>(null);
    const attachmentOffcanvas = ref<InstanceType<typeof ProjectProcureAttachmentOffcanvasComponent>>();
    const attachmentModal = ref<InstanceType<typeof ProjectProcureAttachmentModalComponent>>();
    const attachmentForm = ref<InstanceType<typeof ProjectProcureAttachmentFormComponent>>();
    const imageModal = ref<InstanceType<typeof ProjectProcureImageModalComponent>>();

    onMounted(() => {
      if (project.value?.projectStatus.key > 1) {
        store.dispatch(ProcureAction.FIND, { procureId: project.value?.id });
      }
    });

    watch(() => project.value?.projectStatus.key, (key) => {
      if (key > 1) {
        store.dispatch(ProcureAction.FIND, { procureId: project.value?.id });
      }
    });

    const createClick = () => {
      Promise.resolve(store.dispatch(ProcureAction.CLEAR))
        .then(() => projectProcureForm.value?.show());
    };

    const editClick = () => {
      projectProcureForm.value?.show();
    };

    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(ProcureAction.DELETE, { procureId: procure.value?.projectId })
              .then(() => context.emit('refresh'));
          }
        });
    };

    const endDateClick = () => {
      endDateModal.value?.show();
    };

    const createEndDateClick = () => {
      Promise.resolve(selectedEndDate.value = null)
        .then(() => endDateModal.value?.hide())
        .then(() => endDateForm.value?.show());
    };

    const editEndDateClick = (endDateId: number) => {
      Promise.resolve(selectedEndDate.value = endDateId)
        .then(() => endDateModal.value?.hide())
        .then(() => endDateForm.value?.show());
    };

    const endDateClose = () => {
      Promise.resolve(selectedEndDate.value = null)
        .then(() => endDateForm.value?.hide())
        .then(() => endDateModal.value?.show());
    };

    const attachmentClick = () => {
      attachmentModal.value?.show();
    };

    const uploadClick = () => {
      Promise.resolve(attachmentModal.value?.hide())
        .then(() => attachmentForm.value?.show());
    };

    const formHidden = () => {
      Promise.resolve(attachmentForm.value?.hide())
        .then(() => attachmentModal.value?.show());
    };

    const imageClick = () => {
      imageModal.value?.show();
    };

    const showEndDate = () => {
      endDateOffcanvas.value?.show();
    };

    const showAttachment = () => {
      attachmentOffcanvas.value?.show();
    };

    const thaiDate = (date: Date) =>
      dateFormat.toThaiDate(date, 'long');

    const dateDiff = (date1: Date, date2: Date) => {
      const millisec1 = date1.getTime();
      const millisec2 = date2.getTime();

      return Math.ceil((millisec1 - millisec2) / (1000 * 60 * 60 * 24)) + 1;
    };

    const refresh = () => {
      store.dispatch(ProcureAction.FIND, { procureId: project.value?.id })
        .then(() => context.emit('refresh'));
    };

    return {
      projectProcureForm,
      endDateOffcanvas,
      endDateModal,
      endDateForm,
      attachmentOffcanvas,
      attachmentModal,
      attachmentForm,
      imageModal,
      project,
      procure,
      canEdit,
      isCanceled,
      isStarted,
      isProcured,
      isCompleted,
      isFinished,
      selectedEndDate,
      createClick,
      editClick,
      deleteClick,
      endDateClick,
      createEndDateClick,
      editEndDateClick,
      endDateClose,
      attachmentClick,
      uploadClick,
      formHidden,
      imageClick,
      showEndDate,
      showAttachment,
      thaiDate,
      dateDiff,
      refresh,
    };
  },
});
